'use client'

import { PropsWithoutRef, useEffect, useRef } from 'react'

import { Listing } from '@/data/getListing'
import { smoothScrollTo } from '@/utils/smoothScrollTo'
import { GlobalAppContext } from '../GlobalAppContext'
import { FloorPlanIcon } from '../icons/FloorPlanIcon'
import { GalleryIcon } from '../icons/GalleryIcon'
import { ThreeDTourIcon } from '../icons/ThreeDTourIcon'
import { VideoIcon } from '../icons/VideoIcon'
import { ModalGalleryContext } from '../ModalGallery/GalleryContext'
import { remapListingAssetsToGallery } from './listingImages'

type ListingHeaderProps = PropsWithoutRef<{
  listing: Listing
}>

export const ListingNav = ({ listing }: ListingHeaderProps) => {
  const ref = useRef<HTMLDivElement>(null)
  const [, sendToModalGallery] = ModalGalleryContext.useActor()
  const [, send] = GlobalAppContext.useActor()

  const { galleryIndex, videoIndex, threeDTourIndex, floorplanIndex } = remapListingAssetsToGallery(listing)

  const sideNav = [{ label: 'Explore', cb: () => smoothScrollTo('Explore') }]

  if (listing.advert_brochure.body.includes('FEATURES')) {
    sideNav.push({ label: 'Features', cb: () => smoothScrollTo('Features') })
  }
  if (listing.advert_brochure.body.includes('LIFESTYLE')) {
    sideNav.push({ label: 'Lifestyle', cb: () => smoothScrollTo('Lifestyle') })
  }

  const galleryNav = [
    {
      label: 'Gallery',
      cb: () => sendToModalGallery({ type: 'Activate gallery', index: galleryIndex }),
      Icon: GalleryIcon,
    },
  ]
  if (threeDTourIndex) {
    galleryNav.push({
      label: '3D tour',
      cb: () => sendToModalGallery({ type: 'Activate gallery', index: threeDTourIndex }),
      Icon: ThreeDTourIcon,
    })
  }

  if (videoIndex) {
    galleryNav.push({
      label: 'Video',
      cb: () => sendToModalGallery({ type: 'Activate gallery', index: videoIndex }),
      Icon: VideoIcon,
    })
  }

  if (floorplanIndex) {
    galleryNav.push({
      label: 'Floorplan',
      cb: () => sendToModalGallery({ type: 'Activate gallery', index: floorplanIndex }),
      Icon: FloorPlanIcon,
    })
  }

  useEffect(() => {
    if (!ref.current) {
      return
    }
    const observer = new IntersectionObserver(
      (entries, _observer) => {
        entries.forEach((entry) => {
          send({ type: entry.isIntersecting ? 'Hide sections nav' : 'Show sections nav' })
        })
      },
      // bring in the side nav when this is basically in the middle + of the screen.
      { rootMargin: '-50% 0px 1000% 0px' },
    )

    observer.observe(ref.current)

    return () => observer.disconnect()
  }, [send])

  return (
    <div className="grid w-full items-center justify-start gap-10 p-4 lg:p-10" ref={ref}>
      <nav className="semibold flex flex-1 justify-center gap-4 font-sans text-sm uppercase tracking-widest xl:gap-8">
        {galleryNav.map((galleryItem, i) => {
          return (
            <a
              key={i}
              href="#"
              onClick={(e) => {
                e.preventDefault()
                galleryItem.cb()
              }}
              className="border-b-2 border-neutral-850 pb-0.5"
            >
              {galleryItem.label}
            </a>
          )
        })}
      </nav>
    </div>
  )
}
